// IsochronePanel Preact component for isochrone controls

import { Signal } from '@preact/signals';
import { ISO_VALUE_DEFAULTS } from '../../constants';
import type { IsoConfig, IsoMethod, IsoGraph, IsoPoint } from '../../types';
import { Panel } from './Panel';
import { panelBody } from './panel';
import {
  ClearButton,
  FilterCheckbox,
  FormField,
  MethodSelect,
  GraphSelect,
  ValueInput,
  CalculateButton,
  StatusFooter,
} from './isochrone';

interface IsochronePanelProps {
  config: Signal<IsoConfig>;
  lastPoint: Signal<IsoPoint | null>;
  loading: Signal<boolean>;
  hasResult: Signal<boolean>;
  resultLabel: Signal<string>;
  hintText: Signal<string>;
  filterMarkers: Signal<boolean>;
  onCalculate: () => void;
  onClear: () => void;
  onFilterChange: (checked: boolean) => void;
}

/**
 * IsochronePanel component for isochrone calculation controls
 */
export function IsochronePanel({
  config,
  lastPoint,
  loading,
  hasResult,
  resultLabel,
  hintText,
  filterMarkers,
  onCalculate,
  onClear,
  onFilterChange,
}: IsochronePanelProps) {
  const handleMethodChange = (method: IsoMethod) => {
    const defaults = ISO_VALUE_DEFAULTS[method];
    config.value = {
      ...config.value,
      method,
      value: defaults.value,
    };
  };

  const handleGraphChange = (graph: IsoGraph) => {
    config.value = { ...config.value, graph };
  };

  const handleValueChange = (value: number) => {
    config.value = { ...config.value, value };
  };

  const unit = ISO_VALUE_DEFAULTS[config.value.method].unit;
  const canCalculate = lastPoint.value !== null && !loading.value;
  const statusMessage = hasResult.value ? resultLabel.value : hintText.value;

  return (
    <Panel
      id="iso-control"
      title="🗺️ Isochrone"
      headerActions={hasResult.value ? <ClearButton onClick={onClear} /> : null}
    >
      <div className={panelBody} data-testid="iso-control-body">
        <FormField label="Type" htmlFor="iso-method" data-testid="iso-field">
          <MethodSelect value={config.value.method} onChange={handleMethodChange} />
        </FormField>

        <FormField label="Mode" htmlFor="iso-graph" data-testid="iso-field">
          <GraphSelect value={config.value.graph} onChange={handleGraphChange} />
        </FormField>

        <FormField label="Valeur" htmlFor="iso-value" data-testid="iso-field">
          <ValueInput value={config.value.value} unit={unit} onChange={handleValueChange} />
        </FormField>

        <FormField label="" htmlFor="" data-testid="iso-field">
          <CalculateButton disabled={!canCalculate} onClick={onCalculate} />
        </FormField>

        <FilterCheckbox
          checked={filterMarkers.value}
          disabled={!hasResult.value}
          onChange={onFilterChange}
        />
      </div>

      <StatusFooter loading={loading.value} message={statusMessage} />
    </Panel>
  );
}
